import React from 'react';

import classNames from 'classnames';
import AddIcon from 'icons/add.svg';
import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import AddedIcon from 'icons/ic-added.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';
import Async from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import tailwindConfig from 'stylesheets/tailwind.config';
import voca from 'voca';

import CheckboxField from 'components/application/CheckboxField';
import CircleQuestion from 'components/application/CircleQuestion';
import Tooltip from 'components/application/Tooltip';

const { colors } = tailwindConfig.theme;

const multiRemoveCustomStyles = {
  valueContainer: (provided, _state) => ({
    ...provided,
    display: 'initial',
    maxWidth: '90%',
    overflow: 'hidden',
    padding: '9px 12px !important',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
};

const invalidStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderColor: `${colors.red[600]} !important`,
    '&:hover': {
      borderColor: `${colors.red[600]} !important`,
    },
  }),
};

const selectDefaultComponentProps = {
  MultiValueLabel,
  NoOptionsMessage,
  ClearIndicator,
  MultiValueRemove,
  DropdownIndicator,
  Option,
};

const selectWithCheckboxComponentProps = {
  MultiValueLabel,
  MultiValueContainer,
  NoOptionsMessage,
  ClearIndicator,
  MultiValueRemove,
  DropdownIndicator,
  Option,
};

export default function CollectionSelect({
  customMargin,
  externalProps = {},
  hideDropdown = false,
  isAsync = false,
  isAutoFocused = false,
  isCheckboxes = false,
  isClearable = false,
  isCreatable = false,
  isDisabled = false,
  isInvalid = false,
  isMulti = false,
  isRequired = false,
  isSearchable = false,
  isSingleSelect = false,
  label,
  maxMenuHeight = 138,
  modifiers,
  name,
  onChange,
  options,
  placeholder = null,
  tooltip,
  tooltipClassName,
  value,
}) {
  const defaultProps = {
    autoFocus: isAutoFocused,
    className: 'collection-select__select-container',
    classNamePrefix: 'collection-select',
    hideDropdown,
    id: voca.snakeCase(name),
    isAsync,
    isCheckboxes,
    isClearable,
    isDisabled,
    isMulti,
    isSearchable,
    maxMenuHeight,
    name,
    onChange,
    placeholder,
    value,
  };

  const assignedProps = Object.assign(defaultProps, externalProps);

  const asyncComponent = (
    <Async
      {...assignedProps}
      components={{
        NoOptionsMessage,
        ClearIndicator,
        DropdownIndicator,
        Option,
      }}
      loadOptions={options}
    />
  );

  const asyncCreatableComponent = (
    <AsyncCreatableSelect
      {...assignedProps}
      components={{
        NoOptionsMessage,
        ClearIndicator,
        DropdownIndicator,
        Option,
      }}
      loadOptions={options}
      styles={isInvalid ? invalidStyles : {}}
    />
  );

  const selectComponent = (
    <Select
      {...assignedProps}
      components={
        isCheckboxes ?
          selectWithCheckboxComponentProps
        : selectDefaultComponentProps
      }
      options={options}
      styles={isCheckboxes && multiRemoveCustomStyles}
    />
  );

  const selectCreatableComponent = (
    <CreatableSelect
      {...assignedProps}
      components={{
        NoOptionsMessage,
        MultiValueRemove,
        DropdownIndicator,
        Option,
      }}
      options={options}
      styles={isInvalid ? invalidStyles : {}}
    />
  );

  const creatableSelect = (
    <CreatableSelect
      {...assignedProps}
      autoFocus={true}
      components={{ NoOptionsMessage, DropdownIndicator, Option }}
      options={options}
      styles={isInvalid ? invalidStyles : {}}
    />
  );

  const renderComponent =
    isAsync ?
      isCreatable ? asyncCreatableComponent
      : asyncComponent
    : isCreatable ?
      isSingleSelect ? creatableSelect
      : selectCreatableComponent
    : selectComponent;

  return (
    <div
      className={classNames(
        'collection-select',
        modifiers
          ?.map((modifier) => `collection-select--${modifier}`)
          .join(' '),
      )}
    >
      <div className={classNames('form-group', customMargin)}>
        {label && (
          <label
            className='collection-select__label tw-font-medium'
            htmlFor={name}
          >
            {label} {isRequired && '*'}
            {tooltip && (
              <Tooltip
                className={tooltipClassName}
                placement='top'
                tooltip={tooltip}
                trigger='hover'
              >
                <CircleQuestion />
              </Tooltip>
            )}
          </label>
        )}
        {renderComponent}
      </div>
    </div>
  );
}

CollectionSelect.propTypes = {
  customMargin: PropTypes.string,
  externalProps: PropTypes.object,
  hideDropdown: PropTypes.bool,
  isAsync: PropTypes.bool,
  isAutoFocused: PropTypes.bool,
  isCheckboxes: PropTypes.bool,
  isClearable: PropTypes.bool,
  isCreatable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isMulti: PropTypes.bool,
  isRequired: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isSingleSelect: PropTypes.bool,
  label: PropTypes.string,
  maxMenuHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  modifiers: PropTypes.array,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  placeholder: PropTypes.string,
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  tooltipClassName: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
};

export function ClearIndicator(props) {
  return (
    <components.ClearIndicator {...props}>
      <span className='collection-select__clear-indicator circle--remove' />
    </components.ClearIndicator>
  );
}

export function DropdownIndicator(props) {
  if (props.selectProps.hideDropdown) return null;

  const chevronClassName =
    props.isDisabled ?
      '[&_polygon]:tw-fill-grey-300'
    : '[&_polygon]:tw-fill-grey-700';

  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ?
          <ChevronUpIcon className={chevronClassName} height={24} width={24} />
        : <ChevronDownIcon
            className={chevronClassName}
            height={24}
            width={24}
          />
        }
      </components.DropdownIndicator>
    )
  );
}

function MultiValueLabel(props) {
  return (
    <components.MultiValueLabel {...props}>
      {props.data.label}
      {props.data.sublabel && (
        <div className='collection-select-multivalue__subtext-container'>
          <span className='truncated-text-container truncated-text-container--auto collection-select-multivalue__subtext'>
            {props.data.sublabel}
          </span>
        </div>
      )}
    </components.MultiValueLabel>
  );
}

function MultiValueRemove(props) {
  return (
    <components.MultiValueRemove {...props}>
      <span className="collection-select__multivalue-remove circle--remove tw-bg-transparent before:tw-bg-grey-700 before:tw-content-[''] after:tw-bg-grey-700 after:tw-content-[''] hover:tw-bg-red-600 hover:before:tw-bg-white hover:after:tw-bg-white" />
    </components.MultiValueRemove>
  );
}

export function MultiValueContainer({ data, selectProps }) {
  const values = selectProps.value;

  if (values) {
    return values[values.length - 1].label === data.label ?
        data.label
      : data.label + ', ';
  } else {
    return null;
  }
}

export function NoOptionsMessage(props) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <components.NoOptionsMessage {...props}>
      <div className='tw-text-grey-400'>{i18nTShared('noMatchingResults')}</div>
      {props.selectProps.additionalNoOptionsMessage && (
        <div className='tw-text-grey-400'>
          {props.selectProps.additionalNoOptionsMessage}
        </div>
      )}
    </components.NoOptionsMessage>
  );
}

export function Option(props) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const isCreate = voca.startsWith(props.children, 'Create');

  return (
    <components.Option {...props}>
      <div
        className={classNames(
          'collection-select__option_container',
          isCreate && 'collection-select__option_container--creatable',
        )}
      >
        {isCreate && (
          <div className='tw-flex'>
            <span className='icon-span'>
              <AddIcon
                className='[&_path]:tw-fill-blue-500'
                height={24}
                width={24}
              />
            </span>
            <span className='truncated-text-container truncated-text-container--width-auto'>
              {props.children.slice(0, -1)}
            </span>
            <span>"</span>
          </div>
        )}
        {props.isDisabled && (
          <div className='collection-select__custom-value-container'>
            <span className='collection-select__auto-content-wrapper'>
              {props.children}
            </span>
            <Tooltip
              className='tooltip-dark--max-w-xxs'
              placement='left'
              tooltip={i18nTShared('alreadyAdded')}
              trigger='hover'
            >
              <span className='collection-select__added-icon-wrapper'>
                <AddedIcon
                  className='[&_circle]:tw-fill-cyan-400'
                  height={16}
                  width={16}
                />
              </span>
            </Tooltip>
          </div>
        )}
        {!isCreate && !props.isDisabled && !props.selectProps.isCheckboxes && (
          <span>{props.children}</span>
        )}
        {props.data.sublabel && (
          <div className='truncated-text-container collection-select-option__subtext'>
            {props.data.sublabel}
          </div>
        )}
        {props.selectProps.isCheckboxes && (
          <CheckboxField
            checked={props.isSelected}
            disabled={false}
            isInputLabelSibling={true}
            label={props.label}
            name={props.data.name}
            onChange={props.setValue}
            value={props.value}
          />
        )}
      </div>
    </components.Option>
  );
}
